<template>
  <div class="pt-5 mt-7">
    <div class="mb-6">
      <h2>{{ $t('CONTENT.payments.rent_payments') }}</h2>
    </div>
    <div class="b-contract__section mb-5"
      v-for="(assetComponent, assetComponentIndex) in assetComponents"
      :data-cy="`payment-asset-component${assetComponentIndex}`"
      :key="assetComponentIndex+'_'+assetComponent.assetId">
      <div class="b-contract__section-title">
        <h3>
          {{ getCurrentAssetName(assetComponent.assetId, assetComponentIndex) }}
          {{ getCurrentAssetComponentName(assetComponent) }}
        </h3>
      </div>
      <div class="b-contract__section-blocks pb-5"
           v-for="(lessor, lessorComponentIdx) in assetComponent.lessorComponentShares"
           :key="assetComponent.assetId+'_'+lessor.lessorId">
        <div class="pl-6 pt-6 b-contract__section-blocks-lessor">
          <p>{{ $t('CONTENT.payments.lessor') }}:<span class="b-form__required"> *</span></p>
          <div class="row">
            <div class="col-4">
              <div class="b-contract__section-blocks-lessor-name">{{getLesseeName(lessor.lessorId)}}</div>
            </div>
            <div class="col-1">
              <button type="button" class="b-btn b-btn_border_none" @click="()=>deleteLessorFromAsset(assetComponentIndex, lessorComponentIdx)" v-show="assetComponents.length > 1">
                <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="delete"/>
              </button>
            </div>
          </div>
          <div class="row pt-2">
            <div class="col-4">
              <label>{{ $t("CONTENT.assets.lessorShareExternalId") }}</label>
              <input type="text" class="b-input" v-model="lessor.externalId"/>
            </div>
          </div>
        </div>
        <div
          class="b-contract__section-block"
          v-for="(payment, index) in lessor.paymentsParameters"
          :data-cy="`payment-${index}`"
          :key="payment.idInDB">
            <PaymentWidget
              :lessorComponentShares="lessor"
              :lessorComponentSharesIndex="lessorComponentIdx"
              :assetComponent="assetComponent"
              :payment="payment"
              :paymentIndex="index"
              :assetComponentIndex="assetComponentIndex"
              :paymentsName="paymentsName"
              :currencyCodes="currencyCodes"
              :periods="periods"
              :ratePeriods="ratePeriods"
              :validationErrors="validationErrors[assetComponentIndex]"
              :importedCourseNames="importedCourseNames"
              @changePaymentData="onChangePaymentData"/>
          <div class="b-contract__card-data-payment-copy">
            <button type="button" class="b-btn mb-3 mr-5" @click="copyPayment(payment, assetComponentIndex, lessorComponentIdx)" data-cy="copy-payment">
              <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="copy"></dynamic-icon>
              {{ $t('CONTENT.payments.copy_payment') }}
            </button>
          </div>
        </div>
        <div>
          <button type="button" class="b-btn ml-5" @click="addPayment(assetComponentIndex, lessorComponentIdx)" data-cy="add-payment">
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"></dynamic-icon>
            {{ $t('CONTENT.payments.add_payment') }}
          </button>
        </div>
      </div>
      <div class="row ml-5 pb-5" v-show="getListLessorsForComponent(assetComponent.lessorComponentShares).length !== 0">
        <div class="col-4">
          <label>{{$t('CONTENT.payments.lessor')}}:<span class="b-form__required"> *</span></label>
          <cool-select :list="getListLessorsForComponent(assetComponent.lessorComponentShares)"
                       option-value="id"
                       option-text="name"
                       :placeholder="$t('CONTENT.choose')"
                       v-model="changedLessor"/>
        </div>
        <div class="col-5 mt-6">
          <button type="button" class="b-btn" @click="addPaymentForLessor(assetComponent)">
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"></dynamic-icon>
            {{ $t('CONTENT.payments.add_lessor') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import PaymentParameters from "../../../../../plugins/assetComponent/models/PaymentParameters";
import PaymentWidget from "../../../../../plugins/assetComponent/components/PaymentWidget";
import CurrencyRate from "../../../../../api/CurrencyRate";
import LessorComponentShares from "../../../../../plugins/assetComponent/models/lessorComponentShares";

export default {
  props: {
    contract: {
      type: Object
    },
    validationErrors: {
      type: Array
    }
  },
  data() {
    return {
      importedCourseNames: [],
      changedLessor: null,
    }
  },
  components: {
    PaymentWidget
  },
  computed: {
    ...mapGetters (["getAssets"]),
    ...mapGetters (["getPaymentNames"]),
    ...mapGetters (["getCurrencyCode"]),
    ...mapGetters (["getFrequencies"]),
    ...mapGetters (["getLeaseFrequencies"]),
    ...mapGetters(["allCounterparties"]),
    periods() {
      return this.getFrequencies;
    },
    ratePeriods() {
      return this.getLeaseFrequencies;
    },
    currencyCodes() {
      return this.getCurrencyCode;
    },
    assetComponents() {
      return this.$store.getters["assetComponentsPlugin/getAllAssetComponents"];
    },
    paymentsName() {
      return this.getPaymentNames;
    },
    lessorCounterpartyIds() {
      return this.contract.lessorCounterpartyIds;
    },
  },
  watch: {
    'assetComponents': 'onAssetsUpdated'
  },
  created() {
    this.createLessorComponentShares();
    this.createPayments();
    this.getImportedCourseNames();
    this.$eventBus.$on('deleteAssetComponent', this.onDeleteAssetComponent);
    this.$eventBus.$on('deleteAsset', this.onDeleteAsset);
    this.$eventBus.$on('addNewLessor', this.addLessorFunc);
  },
  methods: {
    getListLessorsForComponent(lessorComponentShares){
      let arr = [];
      for (let i = 0; i < this.contract.lessorCounterpartyIds.length; i++) {
        if (!lessorComponentShares.find(s => s.lessorId === this.contract.lessorCounterpartyIds[i])) {
          const newEl = {
            name: this.getLesseeName(this.contract.lessorCounterpartyIds[i]),
            id: this.contract.lessorCounterpartyIds[i],
          };
          arr.push(newEl);
        }
      }
      return arr;
    },
    addPaymentForLessor(assetComponent){
      const lessorComponentShares = LessorComponentShares.createNew(this.changedLessor);
      const newPayment = PaymentParameters.createNew();
      lessorComponentShares.paymentsParameters.push(newPayment);

      let lessorsShares = assetComponent.lessorComponentShares;
      lessorsShares.push(lessorComponentShares);

      this.updateAssetLessorComponentSharesInStore(assetComponent, lessorsShares);
    },
    onAssetsUpdated() {
      this.createLessorComponentShares();
      this.createPayments();
    },
    async getImportedCourseNames() {
      const api = new CurrencyRate();
      api.getImportedCourseNames()
        .then((data) => {
          this.importedCourseNames = data.map(elem => ({ name: elem.fileName, value: elem.fileId }));
        });
    },
    createLessorComponentShares() {
      for (let i = 0; i < this.assetComponents.length; i++) {
        if(this.assetComponents[i].lessorComponentShares.length !== 0) continue
        this.createAssetLessorComponentShares(this.assetComponents[i])
      }
    },
    createAssetLessorComponentShares(assetComponent) {
      const lessorComponentShares = this.contract.lessorCounterpartyIds
        .map(id => LessorComponentShares.createNew(id))
      this.updateAssetLessorComponentSharesInStore(assetComponent, lessorComponentShares)
    },
    updateAssetLessorComponentSharesInStore(assetComponent, lessorComponentShares) {
      this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
        name: "lessorComponentShares",
        value: lessorComponentShares,
        assetComponent: assetComponent,
      });
    },
    createPayments() {
      this.assetComponents.forEach((assetComponent, assetComponentIndex) => {
        assetComponent.lessorComponentShares.forEach((lessorComponentShare, lessorComponentShareIndex) => {
          if (lessorComponentShare.paymentsParameters.length > 0) return
          this.$store.commit("assetComponentsPlugin/setNewPayment", {
            index: assetComponentIndex,
            lessorIndex: lessorComponentShareIndex,
            payment: PaymentParameters.createNew()
          });
        })
      })
    },
    getCurrentAssetName(assetId, index) {
      for (let i = 0; i < this.getAssets.length; i++) {
        if (this.getAssets[i].idInDB === assetId) {
          return this.getAssets[i].name;
        }
      }
      return `${this.$tc('CONTENT.assets.asset', 1)} ${index + 1}`;
    },
    getCurrentAssetComponentName(component) {
      if (component.name !== null) {
        return ` - ${component.name}`;
      }
    },
    addPayment(assetComponentIndex, lessorComponentSharesIndex) {
      this.$store.commit('assetComponentsPlugin/setNewPayment', {
        index: assetComponentIndex,
        lessorIndex: lessorComponentSharesIndex,
        payment: PaymentParameters.createNew()
      });
    },
    copyPayment(payment, assetComponentIndex, lessorComponentSharesIndex) {
      this.$store.commit('assetComponentsPlugin/setNewPayment', {
        index: assetComponentIndex,
        lessorIndex: lessorComponentSharesIndex,
        payment: PaymentParameters.copy(payment)
      });
    },
    onChangePaymentData() {
      this.$store.commit('SET_VIEW_STATE_OF_CHARTS', true);
    },
    onDeleteAssetComponent(assetComponentIndex) {
      this.validationErrors.splice(assetComponentIndex, 1);
    },
    onDeleteAsset(assetComponentIndexArray) {
      for (let i = 0; i < this.validationErrors.length; i++) {
        for (let j = 0; j < assetComponentIndexArray.length; j++) {
          if (i === assetComponentIndexArray[j]) {
            this.validationErrors.splice(i, 1);
          }
        }
      }
    },
    getLesseeName(lesseeId) {
      for (let i = 0; i < this.allCounterparties.length; i++) {
        if (this.allCounterparties[i].idInDB === lesseeId) {
          return this.allCounterparties[i].displayName;
        }
      }
    },
    addLessorFunc() {
      this.createLessorComponentShares();
      this.createPayments();
    },
    deleteLessorFromAsset(assetIdx, lessorIdx) {
      const assetComponent = this.assetComponents[assetIdx]
      if(assetComponent === undefined) throw 'wrong assetIdx'
      const lessorComponentShares = [...assetComponent.lessorComponentShares]
      lessorComponentShares.splice(lessorIdx, 1)
      this.$store.commit("assetComponentsPlugin/updateAssetComponent", {
        name: "lessorComponentShares",
        value: lessorComponentShares,
        assetComponent: assetComponent,
      });
    },
    singleLessorAsset(assetComponent) {
      return assetComponent.lessorComponentShares.length === 1
    },
  },
  beforeDestroy() {
    this.$eventBus.$off('deleteAssetComponent', this.onDeleteAssetComponent);
    this.$eventBus.$off('deleteAsset', this.onDeleteAsset);
    this.$eventBus.$off('addNewLessor', this.addLessorFunc);
  }
}
</script>
